<script setup lang="ts">
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Button } from '@/components/ui/button'
  import { ChevronDown, Check } from 'lucide-vue-next'
  import { PlatformSyncStatusEnum } from '@/types/generated'
  import { computed } from 'vue'
  import { FILTER_ALL, type PlatformFilterValue } from './platformFilterTypes'
  import { useTranslation } from '@/composables/useTranslation'

  interface Props {
    modelValue: PlatformFilterValue
    counts?: {
      all: number
      inBoth: number
      onlyJupus: number
      onlyActaport: number
    }
  }

  interface Emits {
    (e: 'update:modelValue', value: PlatformFilterValue): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()

  const { t } = useTranslation({
    en: {
      all: 'All',
      onlyInJupus: 'Only in Jupus',
      onlyInActaport: 'Only in Actaport',
      inBothPlatforms: 'In Both Platforms'
    },
    de: {
      all: 'Alle',
      onlyInJupus: 'Nur in Jupus',
      onlyInActaport: 'Nur in Actaport',
      inBothPlatforms: 'In beiden Plattformen'
    }
  })

  const handleSelect = (value: PlatformFilterValue) => {
    emit('update:modelValue', value)
  }

  const currentLabel = computed(() => {
    if (props.modelValue === FILTER_ALL) return t('all')
    if (props.modelValue === PlatformSyncStatusEnum.IN_BOTH) return t('inBothPlatforms')
    if (props.modelValue === PlatformSyncStatusEnum.ONLY_JUPUS) return t('onlyInJupus')
    if (props.modelValue === PlatformSyncStatusEnum.ONLY_ACTAPORT) return t('onlyInActaport')
    return t('all')
  })
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button
        variant="outline"
        class="h-9 w-[200px] justify-between border border-gray-300"
      >
        <span>{{ currentLabel }}</span>
        <ChevronDown class="h-4 w-4 opacity-50" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      class="w-[200px]"
    >
      <DropdownMenuItem
        class="cursor-pointer"
        @click="handleSelect(FILTER_ALL)"
      >
        <Check
          v-if="modelValue === FILTER_ALL"
          class="mr-2 h-4 w-4"
        />
        <span :class="modelValue === FILTER_ALL ? '' : 'ml-6'">
          {{ t('all') }}
          <span
            v-if="counts"
            class="ml-1"
          >
            ({{ counts.all }})
          </span>
        </span>
      </DropdownMenuItem>
      <DropdownMenuItem
        class="cursor-pointer"
        @click="handleSelect(PlatformSyncStatusEnum.IN_BOTH)"
      >
        <Check
          v-if="modelValue === PlatformSyncStatusEnum.IN_BOTH"
          class="mr-2 h-4 w-4"
        />
        <span :class="modelValue === PlatformSyncStatusEnum.IN_BOTH ? '' : 'ml-6'">
          {{ t('inBothPlatforms') }}
          <span
            v-if="counts"
            class="ml-1"
          >
            ({{ counts.inBoth }})
          </span>
        </span>
      </DropdownMenuItem>
      <DropdownMenuItem
        class="cursor-pointer"
        @click="handleSelect(PlatformSyncStatusEnum.ONLY_JUPUS)"
      >
        <Check
          v-if="modelValue === PlatformSyncStatusEnum.ONLY_JUPUS"
          class="mr-2 h-4 w-4"
        />
        <span :class="modelValue === PlatformSyncStatusEnum.ONLY_JUPUS ? '' : 'ml-6'">
          {{ t('onlyInJupus') }}
          <span
            v-if="counts"
            class="ml-1"
          >
            ({{ counts.onlyJupus }})
          </span>
        </span>
      </DropdownMenuItem>
      <DropdownMenuItem
        class="cursor-pointer"
        @click="handleSelect(PlatformSyncStatusEnum.ONLY_ACTAPORT)"
      >
        <Check
          v-if="modelValue === PlatformSyncStatusEnum.ONLY_ACTAPORT"
          class="mr-2 h-4 w-4"
        />
        <span :class="modelValue === PlatformSyncStatusEnum.ONLY_ACTAPORT ? '' : 'ml-6'">
          {{ t('onlyInActaport') }}
          <span
            v-if="counts"
            class="ml-1"
          >
            ({{ counts.onlyActaport }})
          </span>
        </span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
